'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  Check,
  ChevronRight,
  CreditCard,
  Download,
  Grid3X3,
  Loader2,
  Sparkles,
  User,
  Video,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { Link } from '@/core/i18n/navigation';
import { AIMediaType, AITaskStatus } from '@/extensions/ai/types';
import {
  ImageUploader,
  ImageUploaderValue,
} from '@/shared/blocks/common';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Progress } from '@/shared/components/ui/progress';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { useAppContext } from '@/shared/contexts/app';
import { trackVideoEffects } from '@/shared/lib/analytics';
import { cn } from '@/shared/lib/utils';

export interface VideoEffect {
  id: string;
  name: string;
  preview: string;
  model: string;
  provider: string;
  prompt: string;
  credits?: number;
}

interface VideoEffectsGeneratorProps {
  defaultEffect?: string;
  effects?: VideoEffect[];
  maxSizeMB?: number;
  srOnlyTitle?: string;
}

interface GeneratedVideo {
  id: string;
  url: string;
  effect?: string;
  prompt?: string;
}

interface BackendTask {
  id: string;
  status: string;
  provider: string;
  model: string;
  prompt: string | null;
  taskInfo: string | null;
  taskResult: string | null;
}

const POLL_INTERVAL = 15000;
const GENERATION_TIMEOUT = 600000; // 10 minutes for video

// Default model configuration for video effects
const DEFAULT_MODEL = 'openai/sora-2';
const DEFAULT_PROVIDER = 'replicate';
const DEFAULT_CREDITS = 25; // Sora-2 credits

// Default video effects - Christmas themed
const DEFAULT_EFFECTS: VideoEffect[] = [
  {
    id: 'christmas-cozy',
    name: 'Christmas Cozy',
    preview: 'https://r2.zestygen.com/effects/christmas/christmas-cozy.jpg',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    credits: DEFAULT_CREDITS,
    prompt: `Cinematic holiday portrait video, 5 seconds. Cozy Christmas living room setting. Foreground: Soft bokeh of twinkling gold Christmas lights. Midground: Subject warmly lit with gentle expression. Background: Decorated Christmas tree with ornaments, fireplace glow, frosted window with gentle snowfall outside. Warm fireplace key light from right, soft Christmas tree ambient fill. Colors: deep red, forest green, warm gold, ivory white, amber skin tones. Soft lens diffusion for dreamy atmosphere. Camera: Slow dolly-in, subject tilts head slightly with a gentle smile forming, eyes catch the firelight. Sound: Crackling fireplace, soft jingling bells. Finish: Warm film grain, golden halation on highlights.`,
  }
];

function parseTaskResult(taskResult: string | null): any {
  if (!taskResult) {
    return null;
  }

  try {
    return JSON.parse(taskResult);
  } catch (error) {
    console.warn('Failed to parse taskResult:', error);
    return null;
  }
}

function extractVideoUrls(result: any): string[] {
  if (!result) {
    return [];
  }

  // check videos array first
  const videos = result.videos;
  if (videos && Array.isArray(videos)) {
    return videos
      .map((item: any) => {
        if (!item) return null;
        if (typeof item === 'string') return item;
        if (typeof item === 'object') {
          return (
            item.url ?? item.uri ?? item.video ?? item.src ?? item.videoUrl
          );
        }
        return null;
      })
      .filter(Boolean);
  }

  // check output
  const output = result.output ?? result.video ?? result.data;

  if (!output) {
    return [];
  }

  if (typeof output === 'string') {
    return [output];
  }

  if (Array.isArray(output)) {
    return output
      .flatMap((item) => {
        if (!item) return [];
        if (typeof item === 'string') return [item];
        if (typeof item === 'object') {
          const candidate =
            item.url ?? item.uri ?? item.video ?? item.src ?? item.videoUrl;
          return typeof candidate === 'string' ? [candidate] : [];
        }
        return [];
      })
      .filter(Boolean);
  }

  if (typeof output === 'object') {
    const candidate =
      output.url ?? output.uri ?? output.video ?? output.src ?? output.videoUrl;
    if (typeof candidate === 'string') {
      return [candidate];
    }
  }

  return [];
}

export function VideoEffectsGenerator({
  defaultEffect,
  effects = DEFAULT_EFFECTS,
  maxSizeMB = 10,
  srOnlyTitle,
}: VideoEffectsGeneratorProps) {
  const t = useTranslations('video-effects.common.generator');

  const [selectedEffect, setSelectedEffect] = useState<string>(
    defaultEffect || effects[0]?.id || ''
  );
  const [referenceImageItems, setReferenceImageItems] = useState<
    ImageUploaderValue[]
  >([]);
  const [referenceImageUrls, setReferenceImageUrls] = useState<string[]>([]);
  const [generatedVideos, setGeneratedVideos] = useState<GeneratedVideo[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(
    null
  );
  const [taskStatus, setTaskStatus] = useState<AITaskStatus | null>(null);
  const [downloadingVideoId, setDownloadingVideoId] = useState<string | null>(null);
  const [showAllEffectsModal, setShowAllEffectsModal] = useState(false);

  const { user, isCheckSign, setIsShowSignModal, fetchUserCredits } =
    useAppContext();

  const currentEffect = effects.find((e) => e.id === selectedEffect);
  const costCredits = currentEffect?.credits || DEFAULT_CREDITS;
  const remainingCredits = user?.credits?.remainingCredits ?? 0;

  const handleGenerate = async () => {
    if (referenceImageUrls.length === 0) {
      toast.error(t('errors.reference-image-required'));
      return;
    }

    if (!currentEffect) {
      toast.error(t('errors.effect-required'));
      return;
    }

    if (isCheckSign) {
      setIsShowSignModal(true);
      return;
    }

    if (remainingCredits < costCredits) {
      toast.error(t('errors.insufficient-credits'));
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    setTaskStatus(null);
    setGenerationStartTime(Date.now());

    try {
      const options: any = {
        image_input: referenceImageUrls,
      };

      const response = await fetch('/api/ai/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mediaType: AIMediaType.VIDEO,
          scene: 'image-to-video', // Use standard scene for video generation
          provider: currentEffect.provider,
          model: currentEffect.model,
          prompt: currentEffect.prompt,
          options,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('errors.generation-failed'));
      }

      const { code, message, data } = await response.json();
      if (code !== 0) {
        throw new Error(message || t('errors.generation-failed'));
      }

      const newTaskId = data?.id;
      if (!newTaskId) {
        throw new Error('Task id missing in response');
      }

      // Check if task completed immediately
      if (data.status === AITaskStatus.SUCCESS && data.taskInfo) {
        const parsedResult = parseTaskResult(data.taskInfo);
        const videoUrls = extractVideoUrls(parsedResult);

        if (videoUrls.length > 0) {
          const timestamp = Date.now();
          const newVideos: GeneratedVideo[] = videoUrls.map((url, index) => ({
            id: `${newTaskId}-${timestamp}-${index}`,
            url,
            effect: selectedEffect,
            prompt: currentEffect.prompt || undefined,
          }));

          setGeneratedVideos((prev) => [...newVideos, ...prev]);
          toast.success(t('success.video-generated'));

          // Track video effects for analytics
          trackVideoEffects({
            effect: selectedEffect,
            model: currentEffect.model,
            credits_used: costCredits,
          });

          setIsGenerating(false);
          setProgress(100);
          setGenerationStartTime(null);
          setTaskStatus(null);
          await fetchUserCredits();
          return;
        }
      }

      setTaskId(newTaskId);
      await fetchUserCredits();
    } catch (error: any) {
      console.error('Video generation error:', error);
      toast.error(error.message || t('errors.generation-failed'));
      setIsGenerating(false);
      setProgress(0);
      setTaskStatus(null);
      setGenerationStartTime(null);
    }
  };

  useEffect(() => {
    if (!taskId || !isGenerating) {
      return;
    }

    let cancelled = false;

    const pollTask = async (): Promise<boolean> => {
      try {
        // Check for timeout
        if (
          generationStartTime &&
          Date.now() - generationStartTime > GENERATION_TIMEOUT
        ) {
          toast.error(t('errors.generation-timeout'));
          setIsGenerating(false);
          setProgress(0);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          return true;
        }

        const response = await fetch('/api/ai/query', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ taskId }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch task status');
        }

        const { code, message, data } = await response.json();
        if (code !== 0) {
          throw new Error(message || 'Query task failed');
        }

        const task = data as BackendTask;
        const currentStatus = task.status as AITaskStatus;
        setTaskStatus(currentStatus);

        const parsedResult = parseTaskResult(task.taskInfo);
        const videoUrls = extractVideoUrls(parsedResult);

        if (currentStatus === AITaskStatus.PENDING) {
          setProgress((prev) => Math.max(prev, 20));
          return false;
        }

        if (currentStatus === AITaskStatus.PROCESSING) {
          if (videoUrls.length > 0) {
            setProgress((prev) => Math.max(prev, 85));
          } else {
            setProgress((prev) => Math.min(prev + 5, 80));
          }
          return false;
        }

        if (currentStatus === AITaskStatus.SUCCESS) {
          if (videoUrls.length === 0) {
            toast.error(t('errors.no-video-url'));
          } else {
            const timestamp = Date.now();
            const newVideos: GeneratedVideo[] = videoUrls.map((url, index) => ({
              id: `${taskId}-${timestamp}-${index}`,
              url,
              effect: selectedEffect,
              prompt: task.prompt || undefined,
            }));

            setGeneratedVideos((prev) => [...newVideos, ...prev]);
            toast.success(t('success.video-generated'));

            // Track video effects for analytics
            trackVideoEffects({
              effect: selectedEffect,
              model: currentEffect?.model || DEFAULT_MODEL,
              credits_used: costCredits,
            });
          }

          setProgress(100);
          setIsGenerating(false);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          return true;
        }

        if (currentStatus === AITaskStatus.FAILED) {
          const errorMessage = parsedResult?.errorMessage || t('errors.generation-failed');
          toast.error(errorMessage);
          setIsGenerating(false);
          setProgress(0);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          fetchUserCredits();
          return true;
        }

        setProgress((prev) => Math.min(prev + 3, 95));
        return false;
      } catch (error: any) {
        console.error('Error polling task status:', error);
        toast.error(`Query task failed: ${error.message}`);
        setIsGenerating(false);
        setProgress(0);
        setTaskId(null);
        setGenerationStartTime(null);
        setTaskStatus(null);
        fetchUserCredits();
        return true;
      }
    };

    // Initial poll
    pollTask();

    // Set up interval
    const interval = setInterval(async () => {
      if (cancelled || !taskId) {
        clearInterval(interval);
        return;
      }
      const completed = await pollTask();
      if (completed) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [taskId, isGenerating, generationStartTime, selectedEffect, t, fetchUserCredits, costCredits, currentEffect?.model]);

  const handleDownloadVideo = async (video: GeneratedVideo) => {
    if (!video.url) {
      return;
    }

    try {
      setDownloadingVideoId(video.id);
      const response = await fetch(
        `/api/proxy/file?url=${encodeURIComponent(video.url)}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch video');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `zestygen-${video.effect}-${video.id}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
      toast.success(t('success.video-downloaded'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('errors.download-failed'));
    } finally {
      setDownloadingVideoId(null);
    }
  };

  const handleSelectEffect = (effectId: string) => {
    setSelectedEffect(effectId);
    setShowAllEffectsModal(false);
  };

  return (
    <section className="py-16 md:py-24">
      <div className="container">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[300px_minmax(0,1fr)_minmax(0,1fr)]">
            {/* Effects Selector */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold">
                  {t('effects.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                  <div className="grid grid-cols-2 gap-3">
                    {effects.map((effect) => {
                      const isSelected = selectedEffect === effect.id;
                      return (
                        <button
                          key={effect.id}
                          onClick={() => setSelectedEffect(effect.id)}
                          className={cn(
                            'group relative overflow-hidden rounded-lg transition-all duration-200',
                            isSelected
                              ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
                              : 'hover:ring-2 hover:ring-muted-foreground/30 hover:ring-offset-1 hover:ring-offset-background'
                          )}
                        >
                          <div className="relative aspect-square overflow-hidden rounded-t-lg">
                            <img
                              src={effect.preview}
                              alt={effect.name}
                              className={cn(
                                'h-full w-full object-cover transition-transform duration-300',
                                isSelected ? 'scale-105' : 'group-hover:scale-105'
                              )}
                            />
                            {/* Video badge */}
                            <div className="absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white">
                              <Video className="h-3 w-3" />
                            </div>
                            {/* Selected overlay */}
                            {isSelected && (
                              <div className="absolute inset-0 bg-primary/20" />
                            )}
                            {/* Selected checkmark */}
                            {isSelected && (
                              <div className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
                                <Check className="h-3 w-3" />
                              </div>
                            )}
                          </div>
                          <div
                            className={cn(
                              'p-1.5 text-center text-xs font-medium transition-colors',
                              isSelected
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted/50 group-hover:bg-muted'
                            )}
                          >
                            {effect.name}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>

                {/* More Effects Button */}
                <Dialog open={showAllEffectsModal} onOpenChange={setShowAllEffectsModal}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="mt-4 w-full"
                      size="sm"
                    >
                      <Grid3X3 className="mr-2 h-4 w-4" />
                      {t('effects.browse-more')}
                      <ChevronRight className="ml-auto h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[80vh]">
                    <DialogHeader>
                      <DialogTitle>{t('effects.all-effects')}</DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="h-[60vh] pr-4">
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                        {effects.map((effect) => {
                          const isSelected = selectedEffect === effect.id;
                          return (
                            <button
                              key={effect.id}
                              onClick={() => handleSelectEffect(effect.id)}
                              className={cn(
                                'group relative overflow-hidden rounded-xl transition-all duration-200',
                                isSelected
                                  ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
                                  : 'hover:ring-2 hover:ring-muted-foreground/30'
                              )}
                            >
                              <div className="relative aspect-square overflow-hidden rounded-t-xl">
                                <img
                                  src={effect.preview}
                                  alt={effect.name}
                                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                />
                                {/* Video badge */}
                                <div className="absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white">
                                  <Video className="h-4 w-4" />
                                </div>
                                {isSelected && (
                                  <>
                                    <div className="absolute inset-0 bg-primary/20" />
                                    <div className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
                                      <Check className="h-4 w-4" />
                                    </div>
                                  </>
                                )}
                              </div>
                              <div
                                className={cn(
                                  'p-2 text-center text-sm font-medium transition-colors',
                                  isSelected
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted/50 group-hover:bg-muted'
                                )}
                              >
                                {effect.name}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            {/* Generator Controls */}
            <Card>
              <CardHeader>
                {srOnlyTitle && <h2 className="sr-only">{srOnlyTitle}</h2>}
                <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                  {t('title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 pb-8">
                {/* Default Preview - Show selected effect preview when no image uploaded */}
                {referenceImageUrls.length === 0 && currentEffect && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">{t('labels.effect-preview')}</Label>
                    <div className="relative overflow-hidden rounded-lg border-2 border-dashed border-muted-foreground/30">
                      <img
                        src={currentEffect.preview}
                        alt={currentEffect.name}
                        className="h-auto w-full object-cover opacity-70"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-background/50">
                        <p className="text-center text-sm text-muted-foreground px-4">
                          {t('labels.upload-hint')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{t('labels.reference-image')}</Label>
                  <ImageUploader
                    defaultPreviews={referenceImageUrls}
                    onChange={(items) => {
                      setReferenceImageItems(items);
                      const urls = items
                        .filter((item) => item.status === 'uploaded' && item.url)
                        .map((item) => item.url!);
                      setReferenceImageUrls(urls);
                    }}
                    allowMultiple={false}
                    maxImages={1}
                    maxSizeMB={maxSizeMB}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {t('labels.cost')}
                    </span>
                  </div>
                  <span className="text-sm font-semibold">
                    {costCredits} {t('labels.credits')}
                  </span>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {t('labels.remaining-credits')}
                    </span>
                  </div>
                  <span className="text-sm font-semibold">{remainingCredits}</span>
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={
                    isGenerating ||
                    referenceImageUrls.length === 0 ||
                    !currentEffect
                  }
                  className="w-full"
                  size="lg"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('buttons.generating')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      {t('buttons.generate')}
                    </>
                  )}
                </Button>

                {isGenerating && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t('labels.progress')}
                      </span>
                      <span className="font-medium">
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <Progress value={progress} />
                    {taskStatus && (
                      <p className="text-center text-xs text-muted-foreground">
                        {t(`status.${taskStatus.toLowerCase()}`)}
                      </p>
                    )}
                    <p className="text-center text-xs text-muted-foreground">
                      {t('labels.video-generation-time')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Results */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                  <Video className="h-5 w-5" />
                  {t('results.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {generatedVideos.length === 0 ? (
                  <div className="relative min-h-[400px] overflow-hidden rounded-lg border-2 border-dashed">
                    {/* Show effect preview as placeholder */}
                    {currentEffect && (
                      <img
                        src={currentEffect.preview}
                        alt={currentEffect.name}
                        className="absolute inset-0 h-full w-full object-cover opacity-30"
                      />
                    )}
                    <div className="relative flex min-h-[400px] items-center justify-center bg-background/60">
                      <div className="text-center">
                        <Video className="mx-auto h-12 w-12 text-muted-foreground" />
                        <p className="mt-4 text-sm text-muted-foreground">
                          {t('results.no-videos')}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground/70">
                          {t('results.preview-hint')}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <ScrollArea className="h-[600px] pr-4">
                    <div className="space-y-4">
                      {generatedVideos.map((video) => (
                        <div
                          key={video.id}
                          className="group relative overflow-hidden rounded-lg border"
                        >
                          <video
                            src={video.url}
                            controls
                            className="w-full"
                            preload="metadata"
                          />
                          <div className="absolute right-2 top-2 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleDownloadVideo(video)}
                              disabled={downloadingVideoId === video.id}
                            >
                              {downloadingVideoId === video.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}

